import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { Inline, Hidden, Divider } from '@machobear/design-system'
import { Button } from '@/components/button'
import ButtonBar from '@/components/button-bar'
import { confirm } from '@/components/dialog'
import Icon from '@/components/icon'
import { colors } from '@/styles'

const StyledButton = styled(Button)`
  :not(:first-child) {
    margin-left: 8px;
  }
`

const StyledMenuWrapper = styled('div')`
  border-radius: 6px;
  border: 1.5px solid ${colors.grey88};
  background: ${colors.white};
  box-shadow:
    0px 4px 6px -2px rgba(0, 0, 0, 0.05),
    0px 10px 15px -3px rgba(0, 0, 0, 0.1);
  position: absolute;
  bottom: 60px;
`
const StyledMenuButton = styled(Button)`
  color: ${({ color }) => color} !important;
  justify-content: flex-start;
  font-weight: 400px;
  padding: 16px 12px;
  width: 222px;
`

const ListingButtonBar = ({
  listingId,
  saveAndExit,
  handlePreview,
  isSubmitting,
  isDraft,
  children,
  ...rest
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { pathname } = useLocation()
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false)

  const isPreviewPage = pathname.includes('/preview')

  const handleSubMenu = () => {
    setIsSubMenuOpen(!isSubMenuOpen)
  }

  const handleDelete = async () => {
    const { response } = await dispatch.listing.delete(listingId)
    if (response.ok) {
      history.replace('/listings')
    }
  }

  const deleteTitleText = isDraft
    ? t('l.delete_listing_modal.edit.delete_draft.title', 'Delete Draft?')
    : t('l.delete_listing_modal.edit.delete_listing.title', 'Delete Listing?')

  const deleteMessageText = isDraft
    ? t(
        'l.delete_listing_modal.edit.draft_confirmation.text',
        'Are you sure you want to delete this draft?'
      )
    : [
        t(
          'l.delete_listing_modal.edit.confirmation.text',
          'Are you sure you want to delete this listing?'
        ),
        t('l.delete_listing_modal.edit.warning.text', 'All saved data will be lost.')
      ]

  const confirmDelete = () => {
    return confirm(deleteTitleText, deleteMessageText).then(handleDelete)
  }

  return (
    <ButtonBar {...rest} sticky>
      <Inline align="left">
        {!isPreviewPage && (
          <Hidden above="mobile">
            <StyledButton
              theme="secondary"
              size="small"
              disabled={isSubmitting}
              onClick={handleSubMenu}
            >
              {t('l.button_bar.edit.more_options.button', ' More options')}
            </StyledButton>

            {isSubMenuOpen && (
              <StyledMenuWrapper>
                {!!isDraft && (
                  <StyledMenuButton
                    theme="text"
                    color={colors.grey24}
                    disabled={isSubmitting}
                    onClick={handlePreview}
                  >
                    <Icon id="external_link" width="16" />
                    {t('l.listing.sponsored_listing.modal.preview', 'Preview listing')}
                  </StyledMenuButton>
                )}
                <Divider />
                <StyledMenuButton
                  theme="text"
                  tone="critical"
                  color="#FB554B"
                  onClick={confirmDelete}
                >
                  <Icon id="trash_red" width="16" />
                  {t('l.profile.view.menu_delete_listing.button', 'Delete listing')}
                </StyledMenuButton>
              </StyledMenuWrapper>
            )}
          </Hidden>
        )}

        {isPreviewPage && (
          <StyledButton theme="delete" size="small" disabled={isSubmitting} onClick={confirmDelete}>
            {t('common.delete', 'Delete')}
          </StyledButton>
        )}

        {!isPreviewPage && (
          <Hidden below="tablet">
            <StyledButton
              theme="delete"
              size="small"
              disabled={isSubmitting}
              onClick={confirmDelete}
            >
              {t('common.delete', 'Delete')}
            </StyledButton>
          </Hidden>
        )}

        {isDraft && (
          <StyledButton theme="text" size="small" disabled={isSubmitting} onClick={saveAndExit}>
            {t('l.button_bar.edit.save_and_exit.button', 'Save & Exit')}
          </StyledButton>
        )}
      </Inline>
      {children}
    </ButtonBar>
  )
}

export default memo(ListingButtonBar)
